// TROIA-V3-FRONTEND/src/shared/components/ui/ranking/Podium.tsx
import React from 'react';
import type { GamificationRankingScope, RankingEntry } from '@troia-v3/types';

import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui/avatar';
import { Icon } from '@/shared/icons';

import './ranking.css';

export const formatPts = (n: number): string => new Intl.NumberFormat('pt-BR').format(n);
export const initials = (name: string): string => name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? '').join('');

export interface PodiumProps {
  /** Entradas já ordenadas por posição (usa as 3 primeiras). */
  entries: RankingEntry[];
  scope: GamificationRankingScope;
  /**
   * Id do usuário logado. O mockup 09 NUNCA destaca "você" no pódio (só em `.rk-row.me` da lista) —
   * prop mantida na assinatura por paridade de API com `RankingList`, mas não é lida aqui.
   */
  meId?: string;
  testIdPrefix?: string;
}

const SLOTS: Array<{ index: number; cls: string; medal: string }> = [
  { index: 1, cls: 'gm-pslot--second', medal: 'gm-medal--silver' },
  { index: 0, cls: 'gm-pslot--first', medal: 'gm-medal--gold' },
  { index: 2, cls: 'gm-pslot--third', medal: 'gm-medal--bronze' },
];

/** Pódio top 3 — ordem visual 2º · 1º · 3º (mockup 09 `.podium`). */
export const Podium: React.FC<PodiumProps> = ({ entries, scope, testIdPrefix = 'ranking' }) => {
  const isEntityScope = scope === 'teams' || scope === 'units';
  return (
    <div className="gm-podium" data-testid={`${testIdPrefix}-podium`}>
      {SLOTS.map(({ index, cls, medal }) => {
        const entry = entries[index];
        if (!entry) return <div key={index} />;
        const roleText = isEntityScope
          ? `${entry.members ?? 0} vendedores`
          : (entry.subtitle ?? '');
        return (
          <div key={entry.id} className={`gm-pslot ${cls}`} data-testid={`${testIdPrefix}-podium-${entry.position}`}>
            <span className={`gm-pmedal ${medal}`}>{entry.position}</span>
            {/* Equipe/unidade também usa o Avatar shared (fallback slate do DS) — ícone no lugar das iniciais */}
            <Avatar className="gm-pav">
              {!isEntityScope && entry.avatar && <AvatarImage src={entry.avatar} alt={entry.name} />}
              <AvatarFallback>
                {isEntityScope ? <Icon name={scope === 'teams' ? 'users' : 'map-pin'} size={20} /> : initials(entry.name)}
              </AvatarFallback>
            </Avatar>
            <div className="gm-pname">{entry.name}</div>
            {roleText && <div className="gm-prole">{roleText}</div>}
            <div className="gm-ppts">{formatPts(entry.points)}<small>pontos</small></div>
            {!isEntityScope && (
              <div className="gm-pmeta"><span>{entry.leads} leads</span><span>{entry.wins} vendas</span></div>
            )}
          </div>
        );
      })}
    </div>
  );
};
Podium.displayName = 'Podium';
